import styles from "./LessonComments.module.css"

import IconIosChatbubblesOutline from "@shared/components/icons/IosChatbubblesOutline"
import { useDocumentsTogether } from "@shared/components/providers/SupergrainProvider"
import CommentItem from "@shared/unified-tiptap/editor-ui/comments/CommentItem"
import { tracked } from "@supergrain/kernel/react"

interface Props {
  /** The lesson's comment ids (UnifiedCardStack.attributes.commentIds). */
  commentIds: string[] | undefined
  currentUserId: string | null
}

/**
 * Renders the lesson's comments (the comments whose adverbialObject is the lesson as a whole,
 * not a card), above the day's units and the lesson title. Read-only - lesson comments can only
 * be modified in the Instructional Hub.
 */
export default tracked(function LessonComments({ commentIds, currentUserId }: Props) {
  const comments = (useDocumentsTogether("comment", commentIds).value ?? []).filter(
    (comment) =>
      comment.meta.permissions.includes("can-view-comment") && !comment.attributes.deletedAt,
  )

  if (comments.length === 0) return null

  // The white box is this component's own, not the slot's: nothing else in the slot knows
  // whether there is a comment to paint, so a host around it would leave a bare white strip on
  // every lesson that has none. flow-root keeps the list's bottom margin inside the box, which
  // is what stops the course color showing through the gap.
  return (
    <div className="flow-root bg-white px-3 pt-3">
      <div className={styles.commentsList} data-test-lesson-comments>
        <div className={styles.header}>
          <IconIosChatbubblesOutline />
          Lesson Comments
        </div>

        {comments.map((comment) => (
          <CommentItem key={comment.id} comment={comment} currentUserId={currentUserId} />
        ))}
      </div>
    </div>
  )
})
